import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ContractsService } from '../../../services/contracts.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction, TableColumn } from '../../../shared/components/table/table.component';
import { B2B_EXPORT_PAGE_SIZE, exportB2bTableToExcel, rowsFromPagedResponse } from '../../../utils/b2b-table-export.util';

@Component({
  selector: 'app-b2b-contracts-vehicles',
  templateUrl: './b2b-contracts-vehicles.component.html',
  styleUrls: ['./b2b-contracts-vehicles.component.css']
})
export class B2bContractsVehiclesComponent implements OnInit {
  vehicles: any[] = [];
  isLoading = false;
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';

  pageNumber = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  searchTerm = '';

  columns: TableColumn[] = [
    { titulo: 'Placa', label: 'plate' },
    { titulo: 'Modelo', label: 'model' },
    { titulo: 'Ano', label: 'year' },
    { titulo: 'Status', label: 'operationalStatus' },
    { titulo: 'Rastreador', label: 'trackerType' },
    { titulo: 'Pedido', label: 'itemNumber' }
  ];

  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'viewVehicle',
      class: 'btn-view'
    }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadVehicles();
        this.loadContractInfo();
      }
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;

    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (contract) => {
        this.contractNumber = contract.contractNumber || '';
        this.contractName = contract.contractName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  loadVehicles(): void {
    if (!this.contractId) return;

    this.isLoading = true;
    this.contractsService.listB2bContractVehicles(this.contractId, this.pageNumber, this.pageSize, this.searchTerm).subscribe({
      next: (response) => {
        const rows = rowsFromPagedResponse(response);
        this.vehicles = rows.map((v: any) => this.toVehicleRow(v));

        const pag = response?.pagination || {};
        this.totalItems = pag.totalItems || pag.TotalItems || this.vehicles.length;
        this.totalPages = pag.totalPages || pag.TotalPages || Math.max(1, Math.ceil(this.totalItems / Math.max(1, this.pageSize)));
        this.pageNumber = pag.currentPage || pag.CurrentPage || this.pageNumber;
        this.isLoading = false;
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoading = false;
        this.vehicles = [];
        this.totalItems = 0;
      }
    });
  }

  onSearchChange(term: string): void {
    this.searchTerm = (term || '').trim();
    this.pageNumber = 1;
    this.loadVehicles();
  }

  onPageChange(page: number): void {
    if (!page || page === this.pageNumber) return;
    this.pageNumber = page;
    this.loadVehicles();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size > 0 ? size : 10;
    this.pageNumber = 1;
    this.loadVehicles();
  }

  onActionClick(actionData: { action: string; item: any }): void {
    if (actionData.action === 'viewVehicle' && actionData.item?.b2bContractItemId && this.contractId) {
      const itemId = actionData.item.b2bContractItemId;
      const snapshotId = actionData.item?.id;
      if (snapshotId) {
        this.router.navigate(['/b2b/contracts', this.contractId, 'items', itemId, 'vehicles', snapshotId, 'view']);
        return;
      }
      this.router.navigate(['/b2b/contracts', this.contractId, 'items', itemId, 'view']);
    }
  }

  exportExcel(): void {
    if (!this.contractId) return;
    const size = Math.max(this.totalItems || 0, B2B_EXPORT_PAGE_SIZE);
    this.contractsService.listB2bContractVehicles(this.contractId, 1, size, this.searchTerm).subscribe({
      next: (response) => {
        const rows = rowsFromPagedResponse(response).map((v: any) => this.toVehicleRow(v));
        exportB2bTableToExcel(
          `b2b-veiculos-${this.contractNumber || this.contractId}`,
          this.columns,
          rows,
          'Veiculos'
        );
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  private toVehicleRow(v: any): any {
    return {
      id: v.id || v.Id,
      plate: v.plate || v.Plate || '',
      brand: v.brand || v.Brand || '',
      model: v.model || v.Model || '',
      year: v.year || v.Year || '',
      color: v.color || v.Color || '',
      operationalStatus: v.operationalStatus || v.OperationalStatus || '',
      itemNumber: v.itemNumber || v.ItemNumber || v.b2BContractItemId || v.B2BContractItemId || '',
      trackerType: v.trackerType || v.TrackerType || '—',
      trackerId: v.trackerId || v.TrackerId || null,
      b2bContractItemId: v.b2BContractItemId || v.B2BContractItemId || v.b2bContractItemId
    };
  }

  navigateBack(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    } else {
      this.router.navigate(['/b2b/contracts']);
    }
  }
}
